import { useEffect, useState } from 'react'
import { SearchableSelect, Button } from '../../components/BaseComponents'
import CanonicalModal from '../../components/BaseComponents/CanonicalModal/CanonicalModal'
import Label from '../../components/BaseComponents/Label'
import useApi from '../../hooks/useApi'
import { getApiErrorMessage, getCSRFToken } from '../../lib/utils'
import { SelectDropdown } from '../../types/SelectDropdown'

export type AttachableUser = {
	id: string
	fullName: string
	email: string
}

type AddToOrganisationModalProps = {
	user: AttachableUser | null
	onClose: () => void
	onAdded: () => void
}

// B-6a: lets an admin put a registered user (typically one with no organisation) into any organisation.
function AddToOrganisationModal({ user, onClose, onAdded }: AddToOrganisationModalProps) {
	const api = useApi()
	const [companyOptions, setCompanyOptions] = useState<Array<SelectDropdown>>([])
	const [roleOptions, setRoleOptions] = useState<Array<SelectDropdown>>([])
	const [companyId, setCompanyId] = useState('')
	const [roleId, setRoleId] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		if (!user) {
			return
		}
		let cancelled = false
		const run = async () => {
			try {
				const [companies, roles] = await Promise.all([
					api.get(`company?${new URLSearchParams({ page: '1', limit: '1000', orderBy: 'name', direction: 'asc' })}`),
					api.get('roles'),
				])
				if (cancelled) {
					return
				}
				setCompanyOptions((companies.companies?.data ?? []).map((c: any) => ({ label: c.name, value: c.id })))
				const options: Array<SelectDropdown> = (roles.data?.roles ?? [])
					.filter((role: any) => role.name !== 'admin')
					.map((role: any) => ({
						label: role.name.charAt(0).toUpperCase() + role.name.slice(1),
						value: role.id,
					}))
				setRoleOptions(options)
				setRoleId(options.find((option) => option.label === 'Company')?.value ?? '')
			} catch (_) {
				if (!cancelled) {
					setError('Failed to load organisations. Please try again.')
				}
			}
		}
		run()
		return () => {
			cancelled = true
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.id])

	const close = () => {
		setCompanyId('')
		setError(null)
		onClose()
	}

	const submit = async () => {
		if (!user) {
			return
		}
		setSubmitting(true)
		setError(null)
		try {
			await api.post(`admin/companies/${companyId}/users/${user.id}/attach`, {
				_csrf: await getCSRFToken(),
				roleId,
			})
			close()
			onAdded()
		} catch (err) {
			setError(getApiErrorMessage(err, 'Failed to add the user to the organisation. Please try again.'))
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<CanonicalModal
			open={user !== null}
			onClose={close}
			size='small'
			title='Add to organisation'
			subtitle={user ? `${user.fullName} (${user.email})` : undefined}
			actions={
				<div className='flex space-x-3'>
					<Button
						type='Primary'
						size='sm'
						text='Add to organisation'
						disabled={submitting || !companyId || !roleId}
						onClick={submit}
					/>
					<Button type='Outlined' size='sm' text='Cancel' disabled={submitting} onClick={close} />
				</div>
			}
		>
			<div className='mt-6'>
				<Label className='block'>Organisation</Label>
				<SearchableSelect
					name='Organisation'
					options={companyOptions}
					isMulti={false}
					isSearchable
					isClearable={false}
					fillContainer
					value={companyOptions.find((option) => option.value === companyId) ?? null}
					onChange={(option) => setCompanyId(option?.value ?? '')}
				/>
			</div>
			<div className='mt-6'>
				<Label className='block'>Role</Label>
				<SearchableSelect
					name='Role'
					options={roleOptions}
					isMulti={false}
					isSearchable
					isClearable={false}
					fillContainer
					value={roleOptions.find((option) => option.value === roleId) ?? null}
					onChange={(option) => setRoleId(option?.value ?? '')}
				/>
			</div>
			{error && <div className='text-error-500 text-sm mt-2'>{error}</div>}
		</CanonicalModal>
	)
}

export default AddToOrganisationModal
